/**
 * Staff Performance Leaderboard Widget - Premium Liquid Crystal Design
 * =====================================================================
 * Shows top performers by role, productivity rankings,
 * and recognition highlights. Apple-inspired glassmorphism.
 */

import React, { useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import {
  Trophy, Star, TrendingUp, TrendingDown, Medal, Award,
  ChevronRight, Users, Target, Zap, Crown, Flame, Sparkles
} from 'lucide-react';
import { INDUSTRY_STAFF, getIndustryData } from '@/config/industryDemoData';

// Build staff performance from industry data (dynamically)
const buildStaffPerformance = (industry) => {
  const staff = getIndustryData(INDUSTRY_STAFF, industry) || [];
  const top = staff[0] || { name: 'Team Lead' };
  const second = staff[1] || { name: 'Senior Staff' };
  const third = staff[2] || { name: 'Staff' };
  return {
    topPerformers: staff.map(s => ({ ...s, avatar: null })),
    departmentRankings: [
      { dept: staff[0]?.role || 'Team A', avgScore: 94, trend: '+4%', topPerformer: top.name },
      { dept: staff[1]?.role || 'Team B', avgScore: 91, trend: '+2%', topPerformer: second.name },
      { dept: staff[2]?.role || 'Team C', avgScore: 88, trend: '+6%', topPerformer: third.name },
    ],
    recentRecognition: [
      { type: 'achievement', recipient: top.name, award: 'Top Performer of the Month', date: 'Today' },
      { type: 'milestone', recipient: second.name, award: '1 Year Anniversary', date: 'Yesterday' },
      { type: 'kudos', recipient: third.name, award: 'Team Player Award', date: '2 days ago' },
    ],
    improvementNeeded: [
      { id: 10, name: 'New Hire', role: staff[4]?.role || 'Junior', score: 68, issue: 'Onboarding in progress', action: 'Schedule coaching' },
    ]
  };
};

const rankStyles = {
  1: { gradient: 'from-amber-400 to-yellow-500', shadow: 'shadow-amber-500/30', icon: Crown },
  2: { gradient: 'from-gray-300 to-slate-400', shadow: 'shadow-gray-400/30', icon: Medal },
  3: { gradient: 'from-amber-600 to-orange-600', shadow: 'shadow-orange-500/30', icon: Medal },
};

const StaffPerformanceLeaderboard = ({
  industry = 'hospitality',
  className = '',
  compact = false,
  onViewDetails,
  onViewStaff
}) => {
  const [activeTab, setActiveTab] = useState('performers');
  const { topPerformers, departmentRankings, recentRecognition, improvementNeeded } = buildStaffPerformance(industry);

  if (compact) {
    return (
      <div 
        className={`relative overflow-hidden rounded-3xl bg-white/70 backdrop-blur-2xl border border-white/30 shadow-[0_8px_32px_rgb(0,0,0,0.06),inset_0_0_0_1px_rgba(255,255,255,0.5)] p-5 transition-all duration-300 hover:shadow-[0_12px_40px_rgb(0,0,0,0.1)] hover:-translate-y-0.5 ${className}`} 
        data-testid="staff-leaderboard-compact"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-transparent to-transparent pointer-events-none" />
        <div className="relative">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center shadow-lg shadow-amber-500/20">
                <Trophy className="w-4 h-4 text-white" />
              </div>
              <h4 className="font-semibold text-gray-900 tracking-tight">Top Performers</h4>
            </div>
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-100 whitespace-nowrap">This Week</span>
          </div>
          <div className="space-y-2">
            {topPerformers.slice(0, 3).map((staff, idx) => {
              const rankStyle = rankStyles[idx + 1];
              return (
                <div key={staff.id} className="flex items-center gap-3 p-2.5 rounded-2xl bg-gradient-to-r from-gray-50/80 to-white/60 border border-gray-100/50">
                  <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${rankStyle?.gradient || 'from-gray-400 to-gray-500'} flex items-center justify-center shadow-md ${rankStyle?.shadow || ''}`}>
                    <span className="text-xs font-bold text-white">{idx + 1}</span>
                  </div>
                  <span className="text-sm font-medium flex-1 truncate text-gray-900">{staff.name}</span>
                  <span className="text-sm font-bold text-emerald-600">{staff.score}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`relative overflow-hidden rounded-3xl bg-white/70 backdrop-blur-2xl border border-white/30 shadow-[0_8px_32px_rgb(0,0,0,0.06),inset_0_0_0_1px_rgba(255,255,255,0.5)] p-6 transition-all duration-300 hover:shadow-[0_12px_40px_rgb(0,0,0,0.1)] ${className}`} 
      data-testid="staff-leaderboard-widget"
    >
      {/* Background gradient orb */}
      <div className="absolute -top-20 -right-20 w-56 h-56 bg-gradient-to-br from-amber-100/50 to-yellow-200/30 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-transparent to-transparent pointer-events-none" />
      
      <div className="relative">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center shadow-lg shadow-amber-500/25">
              <Trophy className="w-5 h-5 text-white" />
            </div>
            <div className="flex items-center gap-2.5">
              <h3 className="font-semibold text-gray-900 tracking-tight">Staff Performance</h3>
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-100 whitespace-nowrap">This Week</span>
            </div>
          </div>
          <button 
            onClick={onViewDetails}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors whitespace-nowrap"
          >
            Full Report <ChevronRight className="w-3 h-3 ml-1" />
          </button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-3 h-10 mb-4 p-1 bg-gray-100/50 rounded-2xl">
            <TabsTrigger value="performers" className="text-xs gap-1.5 rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <Trophy className="w-3.5 h-3.5" /> Top 5
            </TabsTrigger>
            <TabsTrigger value="departments" className="text-xs gap-1.5 rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <Users className="w-3.5 h-3.5" /> Teams
            </TabsTrigger>
            <TabsTrigger value="recognition" className="text-xs gap-1.5 rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <Award className="w-3.5 h-3.5" /> Recognition
            </TabsTrigger>
          </TabsList>

          {/* Top Performers Tab */}
          <TabsContent value="performers" className="space-y-2.5 mt-0">
            {topPerformers.map((staff, idx) => {
              const rankStyle = rankStyles[idx + 1];
              const RankIcon = rankStyle?.icon || Star;
              return (
                <div 
                  key={staff.id} 
                  className="flex items-center gap-3 p-3 rounded-2xl bg-gradient-to-r from-gray-50/80 to-white/60 border border-gray-100/50 cursor-pointer transition-all hover:shadow-lg hover:-translate-y-0.5 group"
                  onClick={() => onViewStaff?.(staff)}
                >
                  {/* Rank */}
                  <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${rankStyle?.gradient || 'from-gray-400 to-gray-500'} flex items-center justify-center shadow-lg ${rankStyle?.shadow || 'shadow-gray-400/20'}`}>
                    {idx < 3 ? (
                      <RankIcon className="w-4 h-4 text-white" />
                    ) : (
                      <span className="text-sm font-bold text-white">{idx + 1}</span>
                    )}
                  </div>
                  
                  {/* Avatar & Info */}
                  <Avatar className="h-10 w-10 border-2 border-white shadow-md">
                    <AvatarImage src={staff.avatar} />
                    <AvatarFallback className="bg-gradient-to-br from-violet-500 to-purple-600 text-white text-xs font-semibold">
                      {staff.name.split(' ').map(n => n[0]).join('')}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-900 truncate">{staff.name}</span>
                      {staff.streak >= 5 && (
                        <div className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-orange-100">
                          <Flame className="w-3 h-3 text-orange-500" />
                          <span className="text-[10px] font-bold text-orange-600">{staff.streak}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">{staff.role}</span>
                      {staff.badges.slice(0, 1).map(badge => (
                        <span key={badge} className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold bg-violet-100 text-violet-700 border border-violet-200 whitespace-nowrap">
                          {badge}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  {/* Score & Metric */}
                  <div className="text-right">
                    <div className="flex items-center gap-1.5 justify-end">
                      <span className="text-xl font-bold text-gray-900">{staff.score}</span>
                      <span className={`text-xs font-medium ${staff.trend.startsWith('+') ? 'text-emerald-600' : 'text-red-600'}`}>
                        {staff.trend}
                      </span>
                    </div>
                    <p className="text-[10px] text-gray-500">{staff.metric} {staff.metricLabel}</p>
                  </div>
                </div>
              );
            })}
          </TabsContent>

          {/* Department Rankings Tab */}
          <TabsContent value="departments" className="space-y-3 mt-0">
            {departmentRankings.map((dept, idx) => (
              <div key={dept.dept} className="p-4 rounded-2xl bg-gradient-to-r from-gray-50/80 to-white/60 border border-gray-100/50 transition-all hover:shadow-md hover:-translate-y-0.5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${idx === 0 ? 'bg-gradient-to-br from-amber-400 to-yellow-500 shadow-amber-500/20' : 'bg-gradient-to-br from-gray-200 to-gray-300 shadow-gray-300/20'} shadow-lg`}>
                      <span className={`text-xs font-bold ${idx === 0 ? 'text-white' : 'text-gray-600'}`}>
                        {idx + 1}
                      </span>
                    </div>
                    <span className="font-semibold text-gray-900">{dept.dept}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xl font-bold text-gray-900">{dept.avgScore}</span>
                    <span className={`text-xs font-medium ${dept.trend.startsWith('+') ? 'text-emerald-600' : 'text-red-600'}`}>
                      {dept.trend}
                    </span>
                  </div>
                </div>
                <Progress value={dept.avgScore} className="h-2 mb-2" />
                <p className="text-xs text-gray-500">
                  Top performer: <span className="font-medium text-gray-700">{dept.topPerformer}</span>
                </p>
              </div>
            ))}
          </TabsContent>

          {/* Recognition Tab */}
          <TabsContent value="recognition" className="space-y-2.5 mt-0">
            {recentRecognition.map((item, idx) => (
              <div key={`${item?.id ?? item?.name ?? item?.title ?? 'item'}-${idx}`} className="flex items-center gap-3 p-3 rounded-2xl bg-gradient-to-r from-amber-50/80 to-yellow-50/50 border border-amber-100 transition-all hover:shadow-md hover:-translate-y-0.5">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-lg ${
                  item.type === 'achievement' 
                    ? 'bg-gradient-to-br from-amber-400 to-yellow-500 shadow-amber-500/25' 
                    : item.type === 'milestone' 
                    ? 'bg-gradient-to-br from-violet-400 to-purple-500 shadow-violet-500/25'
                    : 'bg-gradient-to-br from-pink-400 to-rose-500 shadow-pink-500/25'
                }`}>
                  {item.type === 'achievement' ? (
                    <Trophy className="w-5 h-5 text-white" />
                  ) : item.type === 'milestone' ? (
                    <Star className="w-5 h-5 text-white" />
                  ) : (
                    <Sparkles className="w-5 h-5 text-white" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900">{item.recipient}</p>
                  <p className="text-xs text-gray-600">{item.award}</p>
                </div>
                <span className="text-[10px] text-gray-500 whitespace-nowrap">{item.date}</span>
              </div>
            ))}
            
            {/* Improvement Needed Section */}
            {improvementNeeded.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200/50">
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Needs Attention</h4>
                {improvementNeeded.map(staff => (
                  <div key={staff.id} className="flex items-center gap-3 p-3 rounded-2xl bg-gradient-to-r from-red-50/80 to-rose-50/50 border border-red-100 mb-2 transition-all hover:shadow-md">
                    <Avatar className="h-9 w-9 border-2 border-white shadow-md">
                      <AvatarFallback className="bg-gradient-to-br from-red-400 to-rose-500 text-white text-xs font-semibold">
                        {staff.name.split(' ').map(n => n[0]).join('')}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900">{staff.name}</p>
                      <p className="text-xs text-red-600">{staff.issue}</p>
                    </div>
                    <button className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-100 hover:bg-red-200 rounded-full transition-colors whitespace-nowrap">
                      {staff.action}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default StaffPerformanceLeaderboard;
